import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { getApiErrorMessage } from '../../utils/apiError';
import { getApiBaseUrl } from '../../utils/apiUrls';

const API_BASE_URL = getApiBaseUrl();

// 비동기 액션들
export const loginWithTwitter = createAsyncThunk(
  'auth/loginWithTwitter',
  async (_, { rejectWithValue }) => {
    try {
      window.location.href = `${API_BASE_URL}/auth/twitter`;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Twitter 로그인 실패'));
    }
  }
);

export const loginWithTelegram = createAsyncThunk(
  'auth/loginWithTelegram',
  async (_, { rejectWithValue }) => {
    try {
      window.location.href = `${API_BASE_URL}/auth/telegram`;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Telegram 로그인 실패'));
    }
  }
);

export const loginWithDiscord = createAsyncThunk(
  'auth/loginWithDiscord',
  async (_, { rejectWithValue }) => {
    try {
      window.location.href = `${API_BASE_URL}/auth/discord`;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Discord 로그인 실패'));
    }
  }
);

export const checkAuthStatus = createAsyncThunk(
  'auth/checkStatus',
  async (_, { rejectWithValue, getState }) => {
    const token = getState().auth.token;
    if (!token) {
      return { authenticated: false, user: null };
    }
    try {
      const response = await axios.get(`${API_BASE_URL}/auth/status`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (error) {
      const status = error.response?.status;
      if (status === 401) {
        return rejectWithValue({ code: 'UNAUTH', message: '세션이 만료되었습니다.' });
      }
      return rejectWithValue({
        code: 'NETWORK',
        message: getApiErrorMessage(error, '인증 상태 확인 실패'),
      });
    }
  }
);

/** 백엔드 User 프로필에 지갑 주소 저장 (MetaMask 연결과 별개) */
export const linkWalletToServer = createAsyncThunk(
  'auth/linkWalletToServer',
  async ({ walletAddress, token }, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/auth/connect-wallet`,
        { walletAddress },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, '지갑 연결 실패'));
    }
  }
);

const storedToken = localStorage.getItem('token') || null;

// 초기 상태 (토큰이 있으면 UI는 로그인으로 두고, DappLayout 에서 checkAuthStatus 로 user 보강)
const initialState = {
  isAuthenticated: Boolean(storedToken),
  user: null,
  token: storedToken,
  loading: false,
  error: null,
};

// 슬라이스 생성
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    // 로그인 성공 처리
    loginSuccess: (state, action) => {
      state.isAuthenticated = true;
      if (action.payload.user) {
        state.user = action.payload.user;
      }
      if (action.payload.token) {
        state.token = action.payload.token;
        localStorage.setItem('token', action.payload.token);
      }
      state.error = null;
    },
    
    // 로그아웃
    logout: (state) => {
      state.isAuthenticated = false;
      state.user = null;
      state.token = null;
      state.error = null;
      localStorage.removeItem('token');
    },
    
    // 에러 초기화
    clearError: (state) => {
      state.error = null;
    },
    
    // 사용자 정보 업데이트
    updateUser: (state, action) => {
      state.user = { ...state.user, ...action.payload };
    },
    
    // 토큰 설정
    setToken: (state, action) => {
      state.token = action.payload;
      localStorage.setItem('token', action.payload);
    }
  },
  extraReducers: (builder) => {
    builder
      // Twitter 로그인
      .addCase(loginWithTwitter.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginWithTwitter.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(loginWithTwitter.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Telegram 로그인
      .addCase(loginWithTelegram.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginWithTelegram.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(loginWithTelegram.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // 인증 상태 확인
      .addCase(checkAuthStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(checkAuthStatus.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = action.payload.authenticated;
        state.user = action.payload.user;
      })
      .addCase(checkAuthStatus.rejected, (state, action) => {
        state.loading = false;
        const payload = action.payload;
        if (payload?.code === 'UNAUTH') {
          state.isAuthenticated = false;
          state.user = null;
          state.token = null;
          localStorage.removeItem('token');
        }
        state.error = typeof payload === 'string' ? payload : payload?.message;
      })
      
      .addCase(linkWalletToServer.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(linkWalletToServer.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload?.user) {
          state.user = { ...state.user, ...action.payload.user };
        }
      })
      .addCase(linkWalletToServer.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  }
});

// 액션 내보내기
export const { 
  loginSuccess, 
  logout, 
  clearError, 
  updateUser, 
  setToken 
} = authSlice.actions;

// 선택자들
export const selectAuth = (state) => state.auth;
export const selectIsAuthenticated = (state) => state.auth.isAuthenticated;
export const selectUser = (state) => state.auth.user;
export const selectToken = (state) => state.auth.token;
export const selectAuthLoading = (state) => state.auth.loading;
export const selectAuthError = (state) => state.auth.error;

// 리듀서 내보내기
export default authSlice.reducer; 